<!-- vueui/src/components/TopNavbar.vue -->
<template>

  <CHeader position="sticky" class="bg-dark px-4 shadow-sm header-with-active-indicator">
    <CContainer fluid class="d-flex justify-content-between align-items-center w-100">

      <!-- Sign Out Modal -->
      <div v-if="signOutModalVisible" class="modal fade show d-block" tabindex="-1"
        style="background-color: rgba(0,0,0,0.5);" @keydown.enter="performSignOut" @keydown.esc="signOutModalVisible = false">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">

            <!-- Header -->
            <div class="modal-header">
              <h5 class="modal-title">Sign Out</h5>
              <button type="button" class="btn-close" @click="signOutModalVisible = false" aria-label="Close"></button>
            </div>

            <!-- Body -->
            <div class="modal-body">
              <p>Are you sure you want to sign out?</p>
              <small class="text-muted">Press Enter to confirm or Escape to cancel</small>
            </div>

            <!-- Footer -->
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="signOutModalVisible = false">Cancel</button>
              <button type="button" class="btn btn-danger text-white" @click="performSignOut">Sign Out</button>
            </div>

          </div>
        </div>
      </div>

      <!-- Left: Logo + user info -->
      <div class="d-flex align-items-center flex-wrap">
        <img :src="'/img/bpmv01w.svg?v=' + imgVersion" width="32" height="32" alt="Logo" class="me-2" />
        <span class="text-warning">BPM</span>
        <span v-if="isSuperUser" class="ms-3 text-light small">
          {{ userEmail }} <span class="text-secondary">({{ userRoleLabel }})</span>
        </span>
      </div>

      <!-- Right: Hamburger menu -->
      <CHeaderToggler class="text-white d-md-none" @click="mobileMenuVisible = !mobileMenuVisible" />

      <!-- Desktop nav -->
      <CHeaderNav class="d-none d-md-flex align-items-center gap-4">
        <RouterLink to="/" class="nav-link text-light">Home</RouterLink>

        <CDropdown v-if="!signedIn || isSuperUser" variant="nav-item" placement="bottom-end">
          <CDropdownToggle 
            color="dark" 
            caret 
            class="text-light dropdown-toggle"
            :class="{ 'dropdown-active': isInfoDropdownActive }"
          >
            Info
          </CDropdownToggle>
          <CDropdownMenu>
            <RouterLink to="/about">
              <CDropdownItem :class="{ 'dropdown-item-active': $route.path === '/about' }">
                About BPM
              </CDropdownItem>
            </RouterLink>
            <RouterLink v-if="isSuperUser" to="/style-guide">
              <CDropdownItem :class="{ 'dropdown-item-active': $route.path === '/style-guide' }">
                Style Guide
              </CDropdownItem>
            </RouterLink>
            <RouterLink to="/privacy-policy">
              <CDropdownItem :class="{ 'dropdown-item-active': $route.path === '/privacy-policy' }">
                Privacy Policy
              </CDropdownItem>
            </RouterLink>
            <RouterLink to="/terms-of-service">
              <CDropdownItem :class="{ 'dropdown-item-active': $route.path === '/terms-of-service' }">
                Terms of Service
              </CDropdownItem>
            </RouterLink>
            <RouterLink to="/contact">
              <CDropdownItem :class="{ 'dropdown-item-active': $route.path === '/contact' }">
                Contact Us
              </CDropdownItem>
            </RouterLink>
          </CDropdownMenu>
        </CDropdown>

        <CDropdown v-if="isSuperUser" variant="nav-item" placement="bottom-end">
          <CDropdownToggle 
            color="dark" 
            caret 
            class="text-light dropdown-toggle"
            :class="{ 
              disabled: !signedIn,
              'dropdown-active': isAdminDropdownActive 
            }"
            :tabindex="signedIn ? 0 : -1" 
            @click.prevent="!signedIn"
          >
            Admin
          </CDropdownToggle>
          <CDropdownMenu>
            <RouterLink to="/users" :class="{ disabled: !isAdminOrEditor }" :tabindex="isAdminOrEditor ? 0 : -1"
              @click.prevent="!isAdminOrEditor">
              <CDropdownItem :class="{ 'dropdown-item-active': $route.path === '/users' }">
                Users
              </CDropdownItem>
            </RouterLink>
            <RouterLink to="/logs" :class="{ disabled: !isAdminOrEditor }" :tabindex="isAdminOrEditor ? 0 : -1"
              @click.prevent="!isAdminOrEditor">
              <CDropdownItem :class="{ 'dropdown-item-active': $route.path === '/logs' }">
                Logs
              </CDropdownItem>
            </RouterLink>
            <RouterLink to="/events" :class="{ disabled: !isAdmin }" :tabindex="isAdmin ? 0 : -1"
              @click.prevent="!isAdmin">
              <CDropdownItem :class="{ 'dropdown-item-active': $route.path === '/events' }">
                Events
              </CDropdownItem>
            </RouterLink>
            <RouterLink to="/business-metrics" :class="{ disabled: !isAdmin }" :tabindex="isAdmin ? 0 : -1"
              @click.prevent="!isAdmin">
              <CDropdownItem :class="{ 'dropdown-item-active': $route.path === '/business-metrics' }">
                Business Metrics
              </CDropdownItem>
            </RouterLink>
            <RouterLink to="/performance-metrics" :class="{ disabled: !isAdmin }" :tabindex="isAdmin ? 0 : -1"
              @click.prevent="!isAdmin">
              <CDropdownItem :class="{ 'dropdown-item-active': $route.path === '/performance-metrics' }">
                Performance Metrics
              </CDropdownItem>
            </RouterLink>
            <RouterLink to="/user-experience" :class="{ disabled: !isAdmin }" :tabindex="isAdmin ? 0 : -1"
              @click.prevent="!isAdmin">
              <CDropdownItem :class="{ 'dropdown-item-active': $route.path === '/user-experience' }">
                User Experience
              </CDropdownItem>
            </RouterLink>
            <RouterLink to="/pending-users" :class="{ disabled: !isAdmin }" :tabindex="isAdmin ? 0 : -1"
              @click.prevent="!isAdmin">
              <CDropdownItem :class="{ 'dropdown-item-active': $route.path === '/pending-users' }">
                Pending Users
              </CDropdownItem>
            </RouterLink>
            <RouterLink to="/pdf-jobs" :class="{ disabled: !isAdminOrEditor }" :tabindex="isAdminOrEditor ? 0 : -1"
              @click.prevent="!isAdminOrEditor">
              <CDropdownItem :class="{ 'dropdown-item-active': $route.path === '/pdf-jobs' }">
                PDF Jobs
              </CDropdownItem>
            </RouterLink>
            <RouterLink to="/development-tasks" :class="{ disabled: !isAdminOrEditor }" :tabindex="isAdminOrEditor ? 0 : -1"
              @click.prevent="!isAdminOrEditor">
              <CDropdownItem :class="{ 'dropdown-item-active': $route.path === '/development-tasks' }">
                Development Tasks
              </CDropdownItem>
            </RouterLink>
            <RouterLink to="/admin-settings" :class="{ disabled: !isAdmin }" :tabindex="isAdmin ? 0 : -1"
              @click.prevent="!isAdmin">
              <CDropdownItem :class="{ 'dropdown-item-active': $route.path === '/admin-settings' }">
                Admin Settings
              </CDropdownItem>
            </RouterLink>
            <RouterLink v-if="isAdmin" to="/register">
              <CDropdownItem :class="{ 'dropdown-item-active': $route.path === '/register' }">
                Register
              </CDropdownItem>
            </RouterLink>
          </CDropdownMenu>
        </CDropdown>

        <RouterLink v-if="signedIn" :to="{ name: 'Dashboard' }" class="nav-link text-light"
          :class="{ disabled: !signedIn }" :tabindex="signedIn ? 0 : -1" @click.prevent="!signedIn">
          Dashboard
        </RouterLink>

        <!-- Notification Bell -->
        <NotificationBell v-if="isSuperUser" />

        <CDropdown variant="nav-item" placement="bottom-end" v-if="isSuperUser">
          <CDropdownToggle 
            color="dark" 
            caret 
            class="text-light dropdown-toggle"
            :class="{ 'dropdown-active': isAccountDropdownActive }"
          >
            Account
          </CDropdownToggle>
          <CDropdownMenu>
            <RouterLink to="/profile">
              <CDropdownItem :class="{ 'dropdown-item-active': $route.path === '/profile' }">
                Profile
              </CDropdownItem>
            </RouterLink>
            <CDropdownItem @click="confirmSignOut" class="text-decoration-underline">
              Sign Out
            </CDropdownItem>
          </CDropdownMenu>
        </CDropdown>

        <a
          v-else-if="signedIn"
          href="#"
          class="nav-link text-light"
          @click.prevent="confirmSignOut"
        >Sign out</a>
        <RouterLink v-else to="/signin" class="nav-link text-light">Sign In</RouterLink>

      </CHeaderNav>

      <CButton class="d-md-none text-warning border-0" @click="mobileMenuVisible = !mobileMenuVisible"
        aria-label="Toggle navigation" style="background: transparent;">
        <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" fill="currentColor" viewBox="0 0 16 16">
          <path fill-rule="evenodd"
            d="M1.5 12a.5.5 0 010-1h13a.5.5 0 010 1h-13zm0-4a.5.5 0 010-1h13a.5.5 0 010 1h-13zm0-4a.5.5 0 010-1h13a.5.5 0 010 1h-13z" />
        </svg>
      </CButton>

    </CContainer>

    <!-- Mobile nav -->
    <div v-if="mobileMenuVisible" class="bg-dark d-md-none px-4 py-3 mobile-nav">
      <RouterLink to="/" class="nav-link text-light mobile-nav-link" @click="mobileMenuVisible = false">Home</RouterLink>
      <template v-if="!signedIn || isSuperUser">
        <RouterLink to="/about" class="nav-link text-light mobile-nav-link" @click="mobileMenuVisible = false">About</RouterLink>
        <RouterLink v-if="isSuperUser" to="/style-guide" class="nav-link text-light mobile-nav-link" @click="mobileMenuVisible = false">Style Guide</RouterLink>
        <RouterLink to="/privacy-policy" class="nav-link text-light mobile-nav-link" @click="mobileMenuVisible = false">Privacy Policy</RouterLink>
        <RouterLink to="/terms-of-service" class="nav-link text-light mobile-nav-link" @click="mobileMenuVisible = false">Terms of Service</RouterLink>
        <RouterLink to="/contact" class="nav-link text-light mobile-nav-link" @click="mobileMenuVisible = false">Contact Us</RouterLink>
      </template>
      
      <!-- Mobile Notification Bell -->
      <div v-if="isSuperUser" class="mobile-notification-section">
        <div class="mobile-notification-header">
          <h6 class="text-light mb-2">Notifications</h6>
        </div>
        <NotificationBell />
      </div>
      
      <template v-if="isSuperUser">
        <RouterLink to="/users" class="nav-link mobile-nav-link" :class="{ 'text-light': true, disabled: !isAdminOrEditor }"
          :aria-disabled="!isAdminOrEditor" tabindex="-1" @click="!isAdminOrEditor ? null : (mobileMenuVisible = false)">Users</RouterLink>
        <RouterLink to="/logs" class="nav-link mobile-nav-link" :class="{ 'text-light': true, disabled: !isAdminOrEditor }"
          :aria-disabled="!isAdminOrEditor" tabindex="-1" @click="!isAdminOrEditor ? null : (mobileMenuVisible = false)">Logs</RouterLink>
        <RouterLink to="/events" class="nav-link mobile-nav-link" :class="{ 'text-light': true, disabled: !isAdmin }"
          :aria-disabled="!isAdmin" tabindex="-1" @click="!isAdmin ? null : (mobileMenuVisible = false)">Events</RouterLink>
        <RouterLink to="/business-metrics" class="nav-link mobile-nav-link" :class="{ 'text-light': true, disabled: !isAdmin }"
          :aria-disabled="!isAdmin" tabindex="-1" @click="!isAdmin ? null : (mobileMenuVisible = false)">Business Metrics</RouterLink>
        <RouterLink to="/performance-metrics" class="nav-link mobile-nav-link" :class="{ 'text-light': true, disabled: !isAdmin }"
          :aria-disabled="!isAdmin" tabindex="-1" @click="!isAdmin ? null : (mobileMenuVisible = false)">Performance Metrics</RouterLink>
        <RouterLink to="/user-experience" class="nav-link mobile-nav-link" :class="{ 'text-light': true, disabled: !isAdmin }"
          :aria-disabled="!isAdmin" tabindex="-1" @click="!isAdmin ? null : (mobileMenuVisible = false)">User Experience</RouterLink>
        <RouterLink to="/pending-users" class="nav-link mobile-nav-link" :class="{ 'text-light': true, disabled: !isAdmin }"
          :aria-disabled="!isAdmin" tabindex="-1" @click="!isAdmin ? null : (mobileMenuVisible = false)">Pending Users</RouterLink>
        <RouterLink to="/pdf-jobs" class="nav-link mobile-nav-link" :class="{ 'text-light': true, disabled: !isAdminOrEditor }"
          :aria-disabled="!isAdminOrEditor" tabindex="-1" @click="!isAdminOrEditor ? null : (mobileMenuVisible = false)">PDF Jobs</RouterLink>
        <RouterLink to="/development-tasks" class="nav-link mobile-nav-link" :class="{ 'text-light': true, disabled: !isAdminOrEditor }"
          :aria-disabled="!isAdminOrEditor" tabindex="-1" @click="!isAdminOrEditor ? null : (mobileMenuVisible = false)">Development Tasks</RouterLink>
        <RouterLink v-if="isAdmin" to="/register" class="nav-link text-light mobile-nav-link" @click="mobileMenuVisible = false">Register</RouterLink>
      </template>
      <RouterLink v-if="signedIn" :to="{ name: 'Dashboard' }" class="nav-link text-light mobile-nav-link" @click="mobileMenuVisible = false">
        Dashboard
      </RouterLink>
      <RouterLink v-if="isSuperUser" to="/profile" class="nav-link text-light mobile-nav-link" @click="mobileMenuVisible = false">Profile</RouterLink>
      <a v-if="signedIn" href="#" class="nav-link text-light mobile-nav-link" @click.prevent="confirmSignOut">Sign Out</a>
      <RouterLink v-else to="/signin" class="nav-link text-light mobile-nav-link" @click="mobileMenuVisible = false">Sign In</RouterLink>
    </div>

  </CHeader>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router'
import {
  CHeader,
  CContainer,
  CHeaderNav,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CHeaderToggler,
} from '@coreui/vue'
import { ref, computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import NotificationBell from './NotificationBell.vue'
import { getImgVersion } from '@/assets/common'

import { Role } from '../../../shared/utils/roles'

const auth = useAuthStore()
const { signedIn, email: userEmail, userRoleLabel, role, isSuperUser } = storeToRefs(auth)

// Helper function to safely check if a role string matches a Role enum value
const hasRole = (userRole: string | null, targetRole: Role): boolean => {
  return userRole === targetRole
}

const isAdmin = computed(() => 
  role.value != null && (hasRole(role.value, Role.Admin) || hasRole(role.value, Role.SuperAdmin))
)

const isAdminOrEditor = computed(() =>
  role.value != null && (
    hasRole(role.value, Role.Admin) || 
    hasRole(role.value, Role.SuperAdmin) || 
    hasRole(role.value, Role.Editor) || 
    hasRole(role.value, Role.Author)
  )
)
const isInfoDropdownActive = computed(() => {
  const currentPath = router.currentRoute.value.path
  return ['/about', '/style-guide', '/privacy-policy', '/terms-of-service', '/contact'].includes(currentPath)
})

const isAdminDropdownActive = computed(() => {
  const currentPath = router.currentRoute.value.path
  return ['/users', '/logs', '/register', '/development-tasks'].includes(currentPath)
})

const isAccountDropdownActive = computed(() => {
  const currentPath = router.currentRoute.value.path
  return ['/profile'].includes(currentPath)
})
const mobileMenuVisible = ref(false)
const router = useRouter()
const signOutModalVisible = ref(false)
const imgVersion = ref('1')

onMounted(async () => {
  imgVersion.value = await getImgVersion()
})

function confirmSignOut(): void {
  signOutModalVisible.value = true
}
async function performSignOut(): Promise<void> {
  signOutModalVisible.value = false
  await auth.logout(router)
}
</script>

<style scoped>
.nav-link {
  color: #f8f9fa;
  /* Light text */
}

.nav-link:hover,
.nav-link:focus {
  background-color: rgba(255, 255, 255, 0.1);
  color: #fff;
  border-radius: 6px;
  transition: all 0.2s ease-in-out;
}

/* Mobile Notification Section Styles */
.mobile-notification-section {
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding: 15px 0;
  margin: 15px 0;
}

.mobile-notification-header h6 {
  font-size: 0.9rem;
  font-weight: 600;
  opacity: 0.9;
}

/* Override NotificationBell styles for mobile */
.mobile-notification-section .notification-bell {
  width: 100%;
}

.mobile-notification-section .notification-trigger {
  width: 100%;
  text-align: left;
  padding: 10px 0;
  border-radius: 6px;
  transition: background-color 0.2s;
}

.mobile-notification-section .notification-trigger:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.mobile-notification-section .notification-dropdown {
  position: static;
  width: 100%;
  margin-top: 10px;
  background-color: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  box-shadow: none;
}

.mobile-notification-section .notification-list {
  max-height: 300px;
  overflow-y: auto;
}

.mobile-notification-section .notification-item {
  padding: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.mobile-notification-section .notification-item:last-child {
  border-bottom: none;
}

.nav-link.router-link-active {
  background: linear-gradient(135deg, #ffc107 0%, #ffca2c 100%);
  color: #212529 !important;
  font-weight: 600;
  border-radius: 8px;
  padding: 8px 16px;
  box-shadow: 0 4px 12px rgba(255, 193, 7, 0.3);
  position: relative;
  transition: all 0.3s ease-in-out;
  text-decoration: none;
  min-width: fit-content;
  white-space: nowrap;
}

.nav-link.router-link-active::before {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  height: 3px;
  background: linear-gradient(90deg, #ffc107, #ffca2c);
  border-radius: 2px;
  transform: translateX(-50%) scaleX(0);
  transform-origin: center;
  animation: activeIndicator 0.3s ease-out forwards;
}

@keyframes activeIndicator {
  to {
    transform: translateX(-50%) scaleX(1);
  }
}

.nav-link.router-link-active:hover {
  background: linear-gradient(135deg, #ffca2c 0%, #ffd54f 100%);
  box-shadow: 0 6px 16px rgba(255, 193, 7, 0.4);
  transform: translateY(-1px);
}

/* Mobile navigation enhancements */
.mobile-nav {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.mobile-nav-link {
  display: block;
  padding: 12px 16px;
  margin: 4px 0;
  border-radius: 8px;
  transition: all 0.2s ease-in-out;
}

.mobile-nav-link:hover {
  background-color: rgba(255, 255, 255, 0.1);
  transform: translateX(4px);
}

.mobile-nav-link.router-link-active {
  background: linear-gradient(135deg, #ffc107 0%, #ffca2c 100%);
  color: #212529 !important;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(255, 193, 7, 0.3);
  transform: translateX(8px);
}

.mobile-nav-link.router-link-active::before {
  display: none;
}

/* Enhanced dropdown styling */
.dropdown-toggle {
  transition: all 0.2s ease-in-out;
}

.dropdown-toggle:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 6px;
}

/* Active dropdown parent styling */
.dropdown-toggle.dropdown-active {
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.2) 0%, rgba(255, 202, 44, 0.2) 100%);
  color: #ffc107 !important;
  border-radius: 6px;
  font-weight: 600;
  position: relative;
}

.dropdown-toggle.dropdown-active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
  width: 60%;
  height: 2px;
  background: linear-gradient(90deg, #ffc107, #ffca2c);
  border-radius: 1px;
  transform: translateX(-50%) scaleX(1);
  transform-origin: center;
}

/* Active dropdown item styling */
.dropdown-item-active {
  background: linear-gradient(135deg, #ffc107 0%, #ffca2c 100%) !important;
  color: #212529 !important;
  font-weight: 600;
  border-radius: 4px;
  margin: 2px 4px;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.3);
  min-width: fit-content;
  white-space: nowrap;
}

.dropdown-item-active:hover {
  background: linear-gradient(135deg, #ffca2c 0%, #ffd54f 100%) !important;
  transform: translateX(2px);
}

/* Smooth transitions for all interactive elements */
.nav-link,
.dropdown-toggle,
.dropdown-item {
  transition: all 0.2s ease-in-out;
}

/* Ensure proper spacing for active indicator */
.header-with-active-indicator {
  padding-bottom: 8px;
}

.nav-link.router-link-active {
  margin-bottom: 4px;
  position: relative;
  z-index: 1;
}

.disabled {
  pointer-events: none;
  cursor: default;
  opacity: 0.6;
}

.dropdown-item {
  cursor: pointer;
}

.dropdown-item:hover {
  text-decoration: underline;
}
</style>
